import { addDoc, collection, deleteDoc, doc, getDocs, updateDoc } from "firebase/firestore";
import AdminModal from "../../../core/admin/adminModal";
import { db } from "../../../../firebase";
import { useEffect, useState } from "react";
import HeaderInput from "./headerInput";
import { deleteObject, getStorage, ref } from "firebase/storage";
import AdminButton from "../../../core/admin/adminButton";
import AdminBadge from "../../../core/admin/adminBadge";
import AdminEmptyState from "../../../core/admin/adminEmptyState";
import { AdminLoading } from "../../../core/admin/adminSpinner";
import { ImageIcon, TrashIcon } from "../../../../img/svg/adminIcons";

export default function HeaderConfigModal({ close }: { close: () => void }) {

    const [images, setImages] = useState<{ id: string, url: string, display: boolean }[]>([]);
    const [loading, setLoading] = useState(false);
    const [refresh, setRefresh] = useState(false);
    const [onlyOne, setOnlyOne] = useState(false);
    const [delModal, setDelModal] = useState(false);
    const [delImage, setDelImage] = useState<{id: string, url: string, display: boolean} | null>(null);

    useEffect(() => {
        getHeaderImages();
    }, [refresh])

    const getHeaderImages = async () => {
        try {
            setLoading(true);
            const q = collection(db, "headerimages");
            const docsSnap = await getDocs(q);
            if (docsSnap.empty) {
                // ao excluir a última imagem a lista precisa esvaziar (antes a imagem excluída continuava na tela)
                setImages([]);
                setLoading(false);
                return null;
            }
            let docs: { id: string, url: string, display: boolean }[] = [];
            docsSnap.docs.forEach(d => {
                docs.push({
                    id: d.id,
                    url: d.data().url,
                    display: d.data().display
                })
            })

            setImages(docs.filter(d => d?.url));
            setLoading(false);
        } catch (error) {
            console.log(error);
            setLoading(false);
            return null;
        }
    }

    const saveImage = async (url: string) => {
        try {
            await addDoc(collection(db, "headerimages"), {
                url,
                display: false
            })

        } catch (error) {
            console.log(error);
        }
    }

    const activateImage = async (imgId: string, state: boolean) => {
        try {
            if (images.filter(i => i.display).length && state) {
                setOnlyOne(true);
                return null;
            }
            setLoading(true);
            const docRef = doc(db, "headerimages", imgId);
            await updateDoc(docRef, { display: state });
            setRefresh(!refresh);
            setLoading(false);
        } catch (error) {
            setLoading(false);
            console.log(error);
        }
    }

    const deleteImage = async (img: { id: string, display: boolean, url: string }) => {
        setLoading(true);
        const storage = getStorage();
        try {
            const imageRef = ref(storage, img.url);
            await deleteObject(imageRef);
            const docRef = doc(db, "headerimages", img.id);

            await deleteDoc(docRef);
            setRefresh(!refresh);
        } catch (error) {
            console.log(error);
        } finally {
            setDelModal(false);
            setLoading(false);
        }
    }

    const handleDelImage = (img: { id: string, display: boolean, url: string }) => {
        setDelImage(img);
        setDelModal(true);
    }

    return (
        <AdminModal close={close} size="lg" title="Imagem do Header" subtitle="Envie novas imagens e escolha qual fica ativa na página inicial.">
            <div className="flex flex-col gap-6">
                <HeaderInput saveImgUrl={saveImage} loading={loading} setLoading={setLoading} refresh={() => setRefresh(!refresh)} />
                <section>
                    <h3 className="text-xs font-bold uppercase tracking-[0.15em] text-gray-500 mb-3">Imagens enviadas</h3>
                    {
                        loading ?
                            <AdminLoading />
                            : images.length ?
                                <ul className="flex flex-col gap-3">
                                    {
                                        images.map((im, idx) => (
                                            <li key={idx} className={`overflow-hidden rounded-xl border ${im.display ? "border-emerald-300 ring-2 ring-emerald-100" : "border-gray-200"}`}>
                                                <div className="relative">
                                                    <img src={im.url} alt="alt" className="h-[100px] w-full object-cover" />
                                                    <div className="absolute top-0 bg-gradient-to-r from-[#00000077] via-[#000000ee] to-[#00000077] w-full h-full"></div>
                                                    {
                                                        im.display ?
                                                            <span className="absolute top-2 left-2"><AdminBadge tone="success" dot>Ativa</AdminBadge></span>
                                                            : null
                                                    }
                                                </div>
                                                <div className="flex items-center justify-end gap-2 bg-gray-50 px-3 py-2">
                                                    {
                                                        im.display ?
                                                            <AdminButton size="sm" variant="secondary" onClick={() => activateImage(im.id, false)}>Desativar</AdminButton>
                                                            :
                                                            <>
                                                                <AdminButton size="sm" variant="ghost" className="!text-red-600 hover:!bg-red-50" icon={<TrashIcon width="14px" />} onClick={() => handleDelImage(im)}>Excluir</AdminButton>
                                                                <AdminButton size="sm" onClick={() => activateImage(im.id, true)}>Ativar</AdminButton>
                                                            </>
                                                    }
                                                </div>
                                            </li>
                                        ))
                                    }
                                </ul>
                                :
                                <AdminEmptyState icon={<ImageIcon />} title="Nenhuma imagem adicionada" />
                    }
                </section>
            </div>
            {
                onlyOne ?
                    <AdminModal
                        close={() => setOnlyOne(false)}
                        size="sm"
                        title="Apenas uma imagem pode estar ativa por vez!"
                        footer={<AdminButton onClick={() => setOnlyOne(false)}>OK</AdminButton>}
                    >
                        <p className="text-sm text-gray-600">Desative a imagem ativa atual antes de ativar outra.</p>
                    </AdminModal>
                    : null
            }
            {
                delModal && delImage ?
                    <AdminModal
                        close={() => setDelModal(false)}
                        size="sm"
                        title="Deseja realmente excluir esta imagem?"
                        footer={
                            <>
                                <AdminButton variant="secondary" disabled={loading} onClick={() => setDelModal(false)}>Cancelar</AdminButton>
                                <AdminButton variant="danger" loading={loading} onClick={() => deleteImage(delImage)}>Sim</AdminButton>
                            </>
                        }
                    >
                        <img src={delImage.url} alt="imagem a excluir" className="h-24 w-full rounded-lg object-cover" />
                        <p className="mt-3 text-sm text-gray-600">A imagem será removida do armazenamento e não poderá ser recuperada.</p>
                    </AdminModal>
                    : null
            }
        </AdminModal>
    )
}
